/**
 * Which task categories this site can actually dispatch.
 *
 * Read-through to the per-site worker's `task_catalog` projection —
 * static reference data like `usePlaces`, not a live overlay.
 *
 * The value is an INTERSECTION computed by the worker: the categories
 * the site's fleet adapters accept, AND the ones the worker can build
 * a request for, AND the ones the building map offers places for. None
 * of the three is sufficient on its own, which is why this is fetched
 * rather than hardcoded — the dispatch form used to list four
 * categories as literals, three of which the worker rejected, and the
 * fourth of which it accepted everywhere regardless of the site.
 *
 * An EMPTY array is a real answer meaning "nothing is dispatchable
 * here right now", not a failure to load. Callers must distinguish it
 * from `isLoading` and `isError` and say which one they are showing —
 * conflating them is how the form came to claim the hotel's robots
 * could not do a delivery they were configured for.
 */
import { useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { fetchTaskCatalog } from '../v1-client';
import type { SiteId, TaskCatalogEntry } from '../v1';

export function useTaskCatalog() {
  const siteId = useSafeSiteId();
  return useQuery<TaskCatalogEntry[]>({
    queryKey: ['task-catalog', siteId],
    queryFn: () => fetchTaskCatalog(siteId as SiteId),
    enabled: Boolean(siteId),
    // Changes only when the site's fleets or map change, i.e. on a
    // worker restart or map republish — the same cadence as
    // `usePlaces`, whose places are this catalog's operands.
    staleTime: 60_000,
  });
}
